import { isGranted, isSuperadmin } from 'common/helpers.ts';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import type { OPERATION } from 'types/Permission.ts';
import { Spinner } from 'ui/component/Spinner.tsx';
import { AppDataProvider } from '#admin/component/AppDataProvider.tsx';
import { AuthenticatedAppProvider } from '#admin/component/AuthenticatedAppProvider.tsx';
import { AuthGate } from '#admin/component/AuthGate.tsx';
import { Forbidden } from '#admin/component/Forbidden.tsx';
import { Layout } from '#admin/component/Layout.tsx';
import { PasswordRequest } from '#admin/component/PasswordRequest.tsx';
import { PasswordReset } from '#admin/component/PasswordReset.tsx';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';
import { ActivityList } from '#admin/views/ActivityList.tsx';
import { AlertList } from '#admin/views/AlertList.tsx';
import { APIKeyList } from '#admin/views/APIKeyList.tsx';
import { Auth } from '#admin/views/Auth.tsx';
import { FleetManagement } from '#admin/views/FleetManagement.tsx';
import { GroupDiagram } from '#admin/views/GroupDiagram.tsx';
import { GroupList } from '#admin/views/GroupList.tsx';
import { Home } from '#admin/views/Home.tsx';
import { Integrations } from '#admin/views/Integrations.tsx';
import { Maintenance } from '#admin/views/Maintenance.tsx';
import { NotFound } from '#admin/views/NotFound.tsx';
import { NotificationList } from '#admin/views/NotificationList.tsx';
import { Operator } from '#admin/views/Operator.tsx';
import { Ops } from '#admin/views/Ops.tsx';
import { PermissionList } from '#admin/views/PermissionList.tsx';
import { Role } from '#admin/views/Role.tsx';
import { RoleList } from '#admin/views/RoleList.tsx';
import { Settings } from '#admin/views/Settings.tsx';
import { ShipmentDetail } from '#admin/views/Shipment.tsx';
import { ShipmentList } from '#admin/views/ShipmentList.tsx';
import { UserDetail } from '#admin/views/User.tsx';
import { UserList } from '#admin/views/UserList.tsx';
import { VerifyDocument } from '#admin/views/VerifyDocument.tsx';
import { SupportRequestList } from '#admin/views/SupportRequestList.tsx';
import { LegList } from './views/LegList.tsx';

// Wrapper for authenticated routes that preloads user data
const AuthenticatedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => (
  <React.Suspense fallback={<Spinner centered={true} />}>
    <AuthenticatedAppProvider>{element}</AuthenticatedAppProvider>
  </React.Suspense>
);

// Guards for gated pages: render the Forbidden state (instead of hitting the error boundary or showing a broken page)
// when the user lacks access — e.g. an operator whose temporary support grant was revoked. Must be nested inside
// AuthenticatedRoute so useMe()/useEnv() have loaded data.
const SuperadminRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { me } = useMe();
  return isSuperadmin(me) ? element : <Forbidden />;
};

const PermissionRoute: React.FC<{ element: React.ReactElement; operation: OPERATION }> = ({ element, operation }) => {
  const { me } = useMe();
  const env = useEnv();
  return isGranted(me, operation, `${env.SUPERADMIN_GROUP_ID}/*`) ? element : <Forbidden />;
};

export const Router = () => {
  return (
    <BrowserRouter basename={'/'}>
      <ErrorBoundary fallback={<Ops />}>
        <React.Suspense fallback={<Spinner centered={true} />}>
          <AppDataProvider>
            <Routes>
              <Route path={'/'} element={<Layout />}>
                <Route path={'/'} element={<AuthGate ifAuthed={true} element={<Navigate to={'/home'} />} elseTo={'/auth'} />} />
                <Route path={'/auth'} element={<AuthGate ifAuthed={false} element={<Auth onSuccess={'/'} />} elseTo={'/'} />} />
                <Route path={'/password-request'} element={<AuthGate ifAuthed={false} element={<PasswordRequest />} elseTo={'/'} />} />
                <Route path={'/password-reset'} element={<AuthGate ifAuthed={false} element={<PasswordReset />} elseTo={'/'} />} />
                <Route path={'/home'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<Home />} />} elseTo={'/auth'} />} />
                <Route path={'/operator'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<Operator />} />} elseTo={'/auth'} />} />
                <Route path={'/users'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<UserList />} />} elseTo={'/auth'} />} />
                <Route path={'/user/:id?'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<UserDetail />} />} elseTo={'/auth'} />} />
                <Route path={'/shipment/:id'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<ShipmentDetail />} />} elseTo={'/auth'} />} />
                <Route path={'/shipments'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<ShipmentList />} />} elseTo={'/auth'} />} />
                <Route path={'/legs'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<LegList />} />} elseTo={'/auth'} />} />
                <Route
                  path={'/fleet'}
                  element={
                    <AuthGate
                      ifAuthed={true}
                      element={<AuthenticatedRoute element={<PermissionRoute operation={'ViewFleet'} element={<FleetManagement />} />} />}
                      elseTo={'/auth'}
                    />
                  }
                />
                <Route
                  path={'/alerts'}
                  element={
                    <AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<PermissionRoute operation={'ViewAlerts'} element={<AlertList />} />} />} elseTo={'/auth'} />
                  }
                />
                <Route
                  path={'/activities'}
                  element={
                    <AuthGate
                      ifAuthed={true}
                      element={<AuthenticatedRoute element={<PermissionRoute operation={'ViewActivities'} element={<ActivityList />} />} />}
                      elseTo={'/auth'}
                    />
                  }
                />
                <Route
                  path={'/api-keys'}
                  element={
                    <AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<PermissionRoute operation={'ViewApiKeys'} element={<APIKeyList />} />} />} elseTo={'/auth'} />
                  }
                />
                <Route
                  path={'/integrations'}
                  element={
                    <AuthGate
                      ifAuthed={true}
                      element={<AuthenticatedRoute element={<PermissionRoute operation={'ViewIntegrations'} element={<Integrations />} />} />}
                      elseTo={'/auth'}
                    />
                  }
                />
                <Route path={'/permissions'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<PermissionList />} />} elseTo={'/auth'} />} />
                <Route
                  path={'/roles'}
                  element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<SuperadminRoute element={<RoleList />} />} />} elseTo={'/auth'} />}
                />
                <Route
                  path={'/role/:id'}
                  element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<SuperadminRoute element={<Role />} />} />} elseTo={'/auth'} />}
                />
                <Route path={'/groups'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<GroupList />} />} elseTo={'/auth'} />} />
                <Route path={'/groups-hierarchy'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<GroupDiagram />} />} elseTo={'/auth'} />} />
                <Route path={'/notifications'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<NotificationList />} />} elseTo={'/auth'} />} />
                <Route
                  path={'/support-access'}
                  element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<SuperadminRoute element={<SupportRequestList />} />} />} elseTo={'/auth'} />}
                />
                <Route path={'/verify'} element={<VerifyDocument />} />
                <Route path={'/maintenance'} element={<Maintenance />} />
                <Route path={'/settings'} element={<AuthGate ifAuthed={true} element={<AuthenticatedRoute element={<Settings />} />} elseTo={'/auth'} />} />
                <Route path={'*'} element={<NotFound />} />
              </Route>
            </Routes>
          </AppDataProvider>
        </React.Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};
